/**
 * Data-driven asset emitters (q-npc-props-adoption-and-flame): an asset pack's `emitters`
 * map (shared `AssetEmitter`) puts a runtime effect on an asset — today a flickering flame —
 * and may hide the static flame the artist baked into the model, per band.
 *
 * Applied ONCE per cached template by `AssetLoader` (the same moment `decorateLoadedAsset`
 * runs), so every clone inherits it: a placed brazier, a dungeon's wall torches and an NPC's
 * held torch all get their flame from the same declaration, and a held prop's flame follows
 * the hand bone because it is a child of the prop.
 *
 * Two halves, deliberately ordered around `markShared` by the loader:
 *   1. `applyEmitterHides` — BEFORE the template's resources are registered as shared: it
 *      removes the authored flame (a whole mesh by glTF material index, or the triangles of
 *      a region above a height / within a radius of the emitter's axis).
 *   2. `attachEmitters` — AFTER: it adds one effect object per emitter from the installed
 *      factory. The effect's GPU resources are the factory's (shared by every flame), never
 *      the template's, so `stripEmitters` takes them off again before a cache release.
 *
 * Nothing happens without an installed factory (`installEmitterFactory`): a session with
 * flames off (`?flames=off`), a node test and any non-Game consumer see the asset exactly as
 * authored. Node-safe: `three` is used only for its pure maths classes.
 *
 * Import boundary (AD-122): shared types via `@great-cloud/shared/client`.
 */

import { Matrix4, Vector3, type BufferAttribute, type Mesh, type Object3D } from 'three';

import type { AssetEmitter, AssetEmitterHide, AssetEntry } from '@great-cloud/shared/client';

/** Builds the runtime object for one emitter (real: the flame system), or null to skip it. */
export type EmitterFactory = (emitter: AssetEmitter) => Object3D | null;

/** `userData` key every attached emitter object carries (strip + diagnostics). */
export const EMITTER_USERDATA_KEY = 'gcEmitter';
/** `userData` key the glTF loader stamps on a mesh: its glTF material index. */
export const GLTF_MATERIAL_USERDATA_KEY = 'gltfMaterial';

/** What decoration did to one template (`userData.gcEmitters`, read by the scene dump). */
export interface EmitterDecoration {
  band: string;
  flames: number;
  hiddenMeshes: number;
  hiddenTriangles: number;
}

let installed: EmitterFactory | null = null;

/**
 * Install the session's emitter factory (Game does, when flames are on). Returns the
 * uninstaller, which only clears the slot if this factory still holds it.
 */
export function installEmitterFactory(factory: EmitterFactory): () => void {
  installed = factory;
  return () => {
    if (installed === factory) installed = null;
  };
}

/** The installed factory, or null (flames off / not in a Game). */
export function emitterFactory(): EmitterFactory | null {
  return installed;
}

/**
 * The band name a delivery handle is for: `'base'` for the entry's textured `url`, else
 * `lod<N>` — from the file name (`x.lod0.glb`), or the handle's index in `lods[]`.
 */
export function emitterBand(entry: Pick<AssetEntry, 'url' | 'lods'>, handle: string): string {
  if (handle === entry.url) return 'base';
  const named = /\.lod(\d+)\.[a-z0-9]+$/i.exec(handle);
  if (named) return `lod${named[1]}`;
  const index = entry.lods?.indexOf(handle) ?? -1;
  return index >= 0 ? `lod${index}` : 'base';
}

interface MeshLike {
  isMesh?: boolean;
  parent: Object3D | null;
  userData: Record<string, unknown>;
  geometry?: Mesh['geometry'];
  matrixWorld: Matrix4;
}

function meshesOf(root: Object3D): MeshLike[] {
  const out: MeshLike[] = [];
  if (typeof root.traverse !== 'function') return out;
  root.traverse((node) => {
    const mesh = node as unknown as MeshLike;
    if (mesh.isMesh === true && node.userData?.[EMITTER_USERDATA_KEY] !== true) out.push(mesh);
  });
  return out;
}

function ruleApplies(rule: AssetEmitterHide, band: string, mesh: MeshLike): boolean {
  if (rule.band !== undefined && rule.band !== band) return false;
  if (rule.material !== undefined && mesh.userData[GLTF_MATERIAL_USERDATA_KEY] !== rule.material)
    return false;
  return true;
}

/**
 * Drop the triangles of `mesh` whose centroid (in template-root space) lies in the rule's
 * region: at or above `above` (when set) and within `radius` of the emitter's vertical axis
 * (when set). Returns how many were dropped; 0 for a geometry it cannot safely edit.
 */
function hideRegion(
  mesh: MeshLike,
  toRoot: Matrix4,
  rule: AssetEmitterHide,
  emitter: AssetEmitter,
): number {
  const geometry = mesh.geometry;
  const position = geometry?.getAttribute('position') as BufferAttribute | undefined;
  if (!geometry || !position || geometry.groups.length > 0) return 0;
  const index = geometry.getIndex();
  const count = index ? index.count : position.count;
  const local = new Matrix4().multiplyMatrices(toRoot, mesh.matrixWorld);
  const a = new Vector3();
  const b = new Vector3();
  const c = new Vector3();
  const kept: number[] = [];
  let dropped = 0;
  const [ax, , az] = emitter.at;
  for (let i = 0; i + 2 < count; i += 3) {
    const ia = index ? index.getX(i) : i;
    const ib = index ? index.getX(i + 1) : i + 1;
    const ic = index ? index.getX(i + 2) : i + 2;
    a.fromBufferAttribute(position, ia).applyMatrix4(local);
    b.fromBufferAttribute(position, ib).applyMatrix4(local);
    c.fromBufferAttribute(position, ic).applyMatrix4(local);
    const cx = (a.x + b.x + c.x) / 3;
    const cy = (a.y + b.y + c.y) / 3;
    const cz = (a.z + b.z + c.z) / 3;
    const high = rule.above === undefined || cy >= rule.above;
    const near = rule.radius === undefined || Math.hypot(cx - ax, cz - az) <= rule.radius;
    if (high && near) dropped += 1;
    else kept.push(ia, ib, ic);
  }
  if (dropped > 0) geometry.setIndex(kept);
  return dropped;
}

/**
 * Remove the authored static flame a declaration names, for this band. A rule with neither
 * `above` nor `radius` removes every matching mesh; a region rule drops triangles. A mesh
 * left with no triangles is removed. Mutates the TEMPLATE (call before `markShared`).
 */
export function applyEmitterHides(
  template: Object3D,
  emitters: readonly AssetEmitter[] | undefined,
  band: string,
): Pick<EmitterDecoration, 'hiddenMeshes' | 'hiddenTriangles'> {
  const result = { hiddenMeshes: 0, hiddenTriangles: 0 };
  if (emitters === undefined || emitters.length === 0) return result;
  const meshes = meshesOf(template);
  if (meshes.length === 0) return result;
  if (typeof template.updateMatrixWorld === 'function') template.updateMatrixWorld(true);
  const toRoot = new Matrix4().copy(template.matrixWorld).invert();
  const removed = new Set<MeshLike>();
  for (const emitter of emitters) {
    for (const rule of emitter.hide ?? []) {
      for (const mesh of meshes) {
        if (removed.has(mesh) || !ruleApplies(rule, band, mesh)) continue;
        if (rule.above === undefined && rule.radius === undefined) {
          removed.add(mesh);
          continue;
        }
        result.hiddenTriangles += hideRegion(mesh, toRoot, rule, emitter);
        const index = mesh.geometry?.getIndex();
        if (index && index.count === 0) removed.add(mesh);
      }
    }
  }
  for (const mesh of removed) {
    mesh.parent?.remove(mesh as unknown as Object3D);
    result.hiddenMeshes += 1;
  }
  return result;
}

/** Add one factory-built effect per emitter at its asset-local `at`. Returns the count added. */
export function attachEmitters(
  template: Object3D,
  emitters: readonly AssetEmitter[] | undefined,
  factory: EmitterFactory | null = installed,
): number {
  if (factory === null || emitters === undefined || typeof template.add !== 'function') return 0;
  let added = 0;
  for (const emitter of emitters) {
    const effect = factory(emitter);
    if (effect === null) continue;
    effect.position.set(emitter.at[0], emitter.at[1], emitter.at[2]);
    effect.userData[EMITTER_USERDATA_KEY] = true;
    template.add(effect);
    added += 1;
  }
  return added;
}

/**
 * Take every attached emitter object off a template (before the loader releases its
 * resources): the effects' GPU resources belong to their factory, not to the template.
 */
export function stripEmitters(template: Object3D): number {
  if (typeof template.traverse !== 'function') return 0;
  const found: Object3D[] = [];
  template.traverse((node) => {
    // Duck-typed: the tests' plain stubs carry no userData.
    if (node.userData?.[EMITTER_USERDATA_KEY] === true) found.push(node);
  });
  for (const node of found) node.parent?.remove(node);
  return found.length;
}
